<template>
  <JazelAppContainer
    app-name="Inventory Search Engine"
    @update:account-settings="accountSettings.setAccountSettings($event)"
    @update:product-settings="productSettings.setProductSettings($event)"
    @update:session-token="accountSettings.setSessionToken($event)"
    @update:user-role="accountSettings.setUserRole($event)"
    @update:is-client-user="accountSettings.setIsClientUser($event)"
    class="bg-grey-lighten-5"
  >
    <!-- ON-BOARDING -->
    <div v-if="showOnboarding" class="fill-height d-flex align-center justify-center">
      <OnBoarding @step="updateStep($event)" @finished="completeOnboarding" />
    </div>

    <!-- BOOTSTRAP LOADER (resolve account flags before mounting route pages) -->
    <div v-else-if="showBootstrapLoader" class="fill-height d-flex align-center justify-center">
      <div class="d-flex align-center ga-4">
        <v-progress-circular indeterminate color="primary" size="44" />
        <div class="text-body-1">Loading account status…</div>
      </div>
    </div>

    <!-- MAIN APP: only after bootstrap -->
    <div v-else class="d-flex">
      <AppNavigationDrawer
        v-model="drawer"
        :has-unpublished-changes="publishStore.hasAnythingToPublish"
        :pricing-hub-href="pricingHubLaunch.homeUrl.value"
      />
      <div class="flex-grow-1 position-relative w-100" style="min-width: 0">
        <!-- Global Processing Status Banner -->
        <v-banner
          v-if="isProcessing"
          color="info"
          :icon="processingIcon"
          class="processing-banner"
          :text="processingMessage"
        />
        <v-btn
          v-if="mdAndDown"
          class="mobile-menu-btn"
          icon="mdi-menu"
          variant="text"
          @click="drawer = !drawer"
        />
        <router-view />
      </div>
    </div>
  </JazelAppContainer>
</template>

<style scoped>
/* Append JUID inline after the dealer name inside the app bar.
   --juid-label is set via :style binding on JazelAppContainer. */
:deep(.v-app-bar .v-toolbar-title__placeholder small.text-subtitle-2)::after {
  content: var(--juid-label, '');
  font-weight: 400;
  opacity: 0.55;
}

/* Keep the main header stuck to the top while scrolling */
:deep(.v-app-bar) {
  position: sticky !important;
  top: 0;
  z-index: 1001;
}

/* Floating mobile menu button (only shown on mdAndDown) */
.mobile-menu-btn {
  background-color: white;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  position: fixed;
  top: 72px;
  right: 12px;
  z-index: 100;
  backdrop-filter: blur(4px);
}

/* Global Processing Banner */
.processing-banner {
  position: sticky;
  top: 0;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}
</style>

<script lang="ts" setup>
import 'vue3-toastify/dist/index.css'
import { watch, computed, ref, onMounted, onUnmounted, provide } from 'vue'
import { useAccountSettingsStore } from '@/stores/accountSettings'
import { useProductSettingsStore } from '@/stores/productSettings'
import { useAccountCapabilitiesStore } from '@/stores/accountCapabilities'
import { JazelAppContainer } from '@jazel/jazel-vuetify'
import { usePublishStore } from '@/stores/usePublishStore'
import { checkImportFeedJobByCorrelationId, checkPipelineStatus, checkPublishLockStatus } from '@/services/publishService'
import {
  deriveProcessingFromPipelineStatus,
  isTerminalPublishStatus,
  shouldClearActivePublishForUnlockedStatus,
  shouldKeepPollingProcessing,
  shouldReconcileOnSuccess,
} from '@/utils/publishPollingDecision'
import OnBoarding from '@/views/onBoarding.vue'
import router from '@/router'
import { useDisplay } from 'vuetify'
import AppNavigationDrawer from '@/components/navigation/AppNavigationDrawer.vue'
import { usePricingHubLaunch } from '@/composables/usePricingHubLaunch'

const endpoint = import.meta.env.VITE_ISE_BACKEND_URL

const accountSettings = useAccountSettingsStore()
const productSettings = useProductSettingsStore()
const accountCapabilities = useAccountCapabilitiesStore()
const publishStore = usePublishStore()
const pricingHubLaunch = usePricingHubLaunch()

const injectJuidIntoHeader = () => {
  const id = String(accountSettings.accountId || '').trim()
  const existing = document.getElementById('ise-juid-label')
  if (!id) {
    existing?.remove()
    return
  }
  const subtitle = document.querySelector('.v-app-bar .v-toolbar-title__placeholder small.text-subtitle-2')
  if (!subtitle) return
  if (existing) {
    existing.textContent = ` (${id})`
    return
  }
  const span = document.createElement('span')
  span.id = 'ise-juid-label'
  span.textContent = ` - ${id}`
  span.style.cssText = 'font-weight:400;opacity:1;user-select:text;cursor:text;'
  subtitle.appendChild(span)
}

watch(() => accountSettings.accountId, () => { setTimeout(injectJuidIntoHeader, 300) })
onMounted(() => { setTimeout(injectJuidIntoHeader, 500) })

const { mdAndDown } = useDisplay()
const drawer = ref(true)

// Global Processing Status tracking
const isProcessing = ref(false)
const processingStatus = ref<string>('')

// Provide global processing signals to child components (like UnpublishedChanges).
// IMPORTANT: `processingStatus` should always reflect the latest status we observed (including SUCCEEDED/FAILED),
// so fast terminal jobs that complete between polling intervals can still clear "waiting" UI states.
provide('isProcessing', isProcessing)
provide('processingStatus', processingStatus)

const processingMessage = computed(() => {
  const status = processingStatus.value.toUpperCase()
  switch (status) {
    case 'INITIATED':
      return 'Import feed initiated — waiting for processing to start...'
    case 'PROCESSING':
      return 'Processing import feed — this may take a couple of minutes...'
    case 'SKIPPED':
      return 'Publish skipped by setting (pause_publish_by_feed=true).'
    case 'SUCCEEDED':
      return 'Import feed completed successfully.'
    case 'FAILED':
      return 'Import feed processing failed.'
    case 'FLAGGED_LARGE_DELTA':
      return 'Import feed flagged for review — large inventory change detected.'
    default:
      return 'Import feed processing...'
  }
})
const processingIcon = computed(() => {
  return processingStatus.value.toUpperCase() === 'PROCESSING' ? 'mdi-cog' : 'mdi-clock-outline'
})

let statusCheckInterval: ReturnType<typeof setInterval> | null = null

const clearGlobalActivePublishState = () => {
  isProcessing.value = false
  processingStatus.value = ''
  publishStore.clearPendingPublish()
  publishStore.clearPendingRepublish()
}

const checkGlobalProcessingStatus = async () => {
  if (!accountSettings.accountId) return

  try {
    // Preferred global signal: publish lock (single-flight). If present, a publish is in progress.
    const lock = await checkPublishLockStatus(endpoint, accountSettings.accountId)
    const ownerCid = String(lock?.ownerCorrelationId || '').trim()

    if (lock?.locked && !ownerCid) {
      processingStatus.value = 'INITIATED'
      isProcessing.value = true
      return
    }

    if (lock?.locked && ownerCid) {
      // Follow the lock owner's job status (avoids "latest job is SKIPPED" masking the true in-flight publish).
      const job = await checkImportFeedJobByCorrelationId(endpoint, ownerCid)
      const status = String(job?.status || 'INITIATED').toUpperCase()
      processingStatus.value = status

      const isTerminal = isTerminalPublishStatus(status)
      isProcessing.value = !isTerminal

      // Reconcile only when the user explicitly initiated a publish.
      if (
        status === 'SUCCEEDED' &&
        (publishStore.hasPendingPublish || !!productSettings.unpublished?.publishInitiatedAt)
      ) {
        console.log('✅ Feed processing completed successfully, reconciling state...')
        await publishStore.checkAndReconcile(endpoint, accountSettings.accountId, true)
        await publishStore.refreshUnpublishedChangesSummary()
      }
      return
    }

    // No active publish lock. The lock is NOT the source of truth for whether a
    // publish is still running: a run can be mid-pipeline after the lock was
    // released early (e.g. a coordinated sibling-feed pre-pipeline failure) or
    // after the lock TTL expired. Fall back to the job list and KEEP tracking any
    // non-terminal run so polling continues until it reaches a terminal state.
    const pipeline = await checkPipelineStatus(endpoint, accountSettings.accountId)
    const signal = deriveProcessingFromPipelineStatus(pipeline)
    const status = String(pipeline?.status || '').toUpperCase()

    if (shouldClearActivePublishForUnlockedStatus(status)) {
      if (
        shouldReconcileOnSuccess({
          isRunning: false,
          status,
          hasPendingPublish: publishStore.hasPendingPublish === true,
          publishInitiatedAt: productSettings.unpublished?.publishInitiatedAt ?? null,
        })
      ) {
        console.log('✅ Feed processing completed successfully, reconciling state...')
        await publishStore.checkAndReconcile(endpoint, accountSettings.accountId, true)
        await publishStore.refreshUnpublishedChangesSummary()
      } else if (publishStore.hasPendingPublish) {
        await publishStore.refreshUnpublishedChangesSummary().catch(() => {
          // non-fatal; clearing active publish state must not depend on summary refresh.
        })
      }

      clearGlobalActivePublishState()
      return
    }

    isProcessing.value = signal.isProcessing
    processingStatus.value = signal.processingStatus

    // Only reconcile/clear pending state when nothing is running AND the latest run
    // succeeded. Never clear pending-publish state just because an unrelated run
    // finished while our run is still PROCESSING.
    if (
      shouldReconcileOnSuccess({
        isRunning: signal.isProcessing,
        status: pipeline?.status,
        hasPendingPublish: publishStore.hasPendingPublish === true,
        publishInitiatedAt: productSettings.unpublished?.publishInitiatedAt ?? null,
      })
    ) {
      console.log('✅ Feed processing completed successfully, reconciling state...')
      await publishStore.checkAndReconcile(endpoint, accountSettings.accountId, true)
      await publishStore.refreshUnpublishedChangesSummary()
    }
  } catch (error) {
    console.error('Error checking global processing status:', error)
  }
}

const startGlobalStatusPolling = () => {
  // Check immediately
  checkGlobalProcessingStatus()

  // Then check every 30 seconds
  if (!statusCheckInterval) {
    statusCheckInterval = setInterval(checkGlobalProcessingStatus, 30000)
  }
}

const stopGlobalStatusPolling = () => {
  if (statusCheckInterval) {
    clearInterval(statusCheckInterval)
    statusCheckInterval = null
  }
}

// Cleanup on unmount
onUnmounted(() => {
  stopGlobalStatusPolling()
})

// Bootstrapping:
// Before we allow any route-level pages to mount, we determine:
// 1) onboarding state (from product settings)
// 2) publish in progress (pipeline status)
// 3) unpublished changes exist (server-truth summary)
const bootReady = ref(false)
const productSettingsLoaded = computed(() => productSettings.hasLoaded === true)
const showOnboarding = computed(() => productSettingsLoaded.value && productSettings.isOnBoarding)
const showBootstrapLoader = computed(
  () => !productSettingsLoaded.value || (productSettingsLoaded.value && !productSettings.isOnBoarding && !bootReady.value),
)

const updateStep = async (step: any) => {
  const nextStep = Math.max(1, Math.min(5, Number(step) || 1))

  productSettings.setOnboardingStep(nextStep)
  productSettings.setIsOnBoarding(true)

  await productSettings.saveSettings({
    isOnBoarding: true,
    step: nextStep,
  })
}

let isCompletingOnboarding = false

const completeOnboarding = async () => {
  if (isCompletingOnboarding) return
  isCompletingOnboarding = true
  try {
    productSettings.setOnboardingStep(5)
    productSettings.setIsOnBoarding(false)

    await productSettings.saveSettings({
      isOnBoarding: false,
      step: 5,
    })

    await router.push('/')
  } finally {
    isCompletingOnboarding = false
  }
}

// Helper function to initialize/update the published snapshot
async function initializePublishedSnapshot(timestamp: string) {
  try {
    // Dynamically import stores to avoid circular dependencies
    const { useVehicleFieldMappingStore } = await import('@/stores/vehicleFieldMapping')
    const { useSegmentStore } = await import('@/stores/segment')
    const { useListingConfigStore, DEFAULT_LISTING_RULES } = await import('@/stores/listingConfig')

    const fieldMappingStore = useVehicleFieldMappingStore()
    const segmentStore = useSegmentStore()
    const listingStore = useListingConfigStore()

    // Always fetch latest configs so the snapshot reflects CURRENT JAMS state,
    // not whatever happened earlier in the onboarding flow.
    // Each store's getter applies defaults if data is missing.
    await fieldMappingStore.getMappingConfig()
    await segmentStore.getMappingConfig()
    await listingStore.getListingConfig()

    // Ensure we have segments loaded into the UI store for snapshot building;
    // this may be skipped if they've already been loaded elsewhere.
    if (!segmentStore.segments || segmentStore.segments.length === 0) {
      await segmentStore.getSegments()
    }

    // Build the snapshot, using defaults if any config is missing
    // Field Mapping: Use originalConfig from JAMS (has segments under mapping_config)
    // If missing, the store's ensureDefaultConfig will have created a default
    const fieldMappingSnapshot = fieldMappingStore.originalConfig
      ? JSON.parse(JSON.stringify(fieldMappingStore.originalConfig))
      : null

    // Segments: Convert from UI array format back to config object format
    // The store loads segments as an array, but we need to store them as an object for comparison
    let segmentsSnapshot = null
    if (segmentStore.segments && segmentStore.segments.length > 0) {
      segmentsSnapshot = segmentStore.segments.reduce((acc: any, seg: any) => {
        acc[seg.id] = {
          name: seg.name,
          priority: seg.priority,
          custom: seg.custom,
          ruleset: {
            logic: 'AND',
            rules: (seg.criteria || [])
              .filter((c: any) => c.field && c.operator) // Filter out incomplete rules
              .map((c: any) => ({
                type: 'rule',
                field: c.field,
                operator: c.operator,
                value: c.value,
              })),
          },
        }
        return acc
      }, {})
    } else {
      // Use minimal fallback if none exist (will be fetched from backend if needed)
      segmentsSnapshot = {
        all: {
          name: 'All Vehicles',
          exclusive: false,
          priority: 1000,
          custom: false,
          ruleset: { logic: 'AND', rules: [] }
        }
      }
    }

    // Publish Rules: Use the config from the store
    // The store's ensureDefaultConfig will have applied defaults if missing
    const listingRulesSnapshot = listingStore.listingConfig
      ? JSON.parse(JSON.stringify(listingStore.listingConfig))
      : JSON.parse(JSON.stringify(DEFAULT_LISTING_RULES))

    // Keep snapshot local-only (no JAMS persistence).
    // This avoids direct JAMS PUTs from UI and prevents config clobbering.
    productSettings.publishedSnapshot = {
      lastPublishedAt: timestamp,
      fieldMapping: fieldMappingSnapshot,
      segments: segmentsSnapshot,
      listingRules: listingRulesSnapshot,
    }
  } catch (error) {
    console.error('Error updating published snapshot:', error)
  }
}

const lastBootAccountId = ref<string>('')
const lastBootToken = ref<string>('')

const shouldPollProcessing = () =>
  shouldKeepPollingProcessing({
    isProcessing: isProcessing.value === true,
    hasPendingPublish: publishStore.hasPendingPublish === true,
    hasPendingRepublish: publishStore.hasPendingRepublish === true,
    routePath: String(router.currentRoute.value?.path || ''),
  })

const syncProcessingPolling = () => {
  if (!accountSettings.accountId) {
    stopGlobalStatusPolling()
    return
  }
  if (shouldPollProcessing()) {
    startGlobalStatusPolling()
  } else {
    stopGlobalStatusPolling()
  }
}

const runBootstrap = async () => {
  // If product settings haven't loaded yet, we can't know onboarding state.
  if (!productSettingsLoaded.value) {
    bootReady.value = false
    stopGlobalStatusPolling()
    isProcessing.value = false
    processingStatus.value = ''
    return
  }

  // During onboarding, do not perform main-app boot network calls.
  if (productSettings.isOnBoarding) {
    bootReady.value = false
    stopGlobalStatusPolling()
    isProcessing.value = false
    processingStatus.value = ''
    return
  }

  const accountId = String(accountSettings.accountId || '').trim()
  const token = String(accountSettings.sessionToken || '').trim()
  if (!accountId || !token) {
    accountCapabilities.reset()
    bootReady.value = false
    stopGlobalStatusPolling()
    isProcessing.value = false
    processingStatus.value = ''
    return
  }

  const bootKey = `${accountId}|${token}`
  const prevKey = `${lastBootAccountId.value}|${lastBootToken.value}`
  if (bootReady.value && bootKey === prevKey) {
    syncProcessingPolling()
    return
  }

  if (bootKey !== prevKey) {
    accountCapabilities.reset()
  }

  lastBootAccountId.value = accountId
  lastBootToken.value = token
  // Prevent route-level API calls from firing until we're ready.
  bootReady.value = false

  // Required bootstrap calls:
  // - unpublished changes: 1 call (/unpublished-changes/summary)
  // - publish ongoing: 1 call (check-import-feed-status)
  // - account capabilities: 1 call (/pricing-hub-settings/{jazelId})
  await Promise.all([
    publishStore.refreshUnpublishedChangesSummary(),
    checkGlobalProcessingStatus(),
    accountCapabilities.loadForAccount(endpoint, accountId, token),
  ])

  // Ready to mount route pages.
  bootReady.value = true

  // Start/stop polling only when needed (route or in-flight publish).
  syncProcessingPolling()

  // Optional background-only snapshot bootstrap for "published vs current" comparisons.
  try {
    const currentPublishedAt = productSettings.publishedSnapshot?.lastPublishedAt
    const snapshotExists =
      currentPublishedAt && Object.keys(productSettings.publishedSnapshot || {}).length > 1

    if (!snapshotExists) {
      void initializePublishedSnapshot(new Date().toISOString())
    }
  } catch (_e) {
    // ignore
  }
}

watch(
  () => [
    productSettingsLoaded.value,
    productSettings.isOnBoarding,
    accountSettings.accountId,
    accountSettings.sessionToken,
  ],
  () => {
    void runBootstrap()
  },
  { immediate: true },
)

// Keep processing polling minimal and route-driven.
watch(
  () => router.currentRoute.value?.path,
  () => syncProcessingPolling(),
)
watch(
  () => publishStore.hasPendingPublish,
  () => syncProcessingPolling(),
)
watch(
  () => publishStore.hasPendingRepublish,
  () => syncProcessingPolling(),
)
// Keep polling alive while any run is in-flight, even when the publish lock is
// already gone (released early or TTL-expired). `syncProcessingPolling` is
// idempotent, so re-evaluating on isProcessing changes cannot double-start.
watch(
  () => isProcessing.value,
  () => syncProcessingPolling(),
)
</script>

<style scoped>
/* Drawer sizing is controlled via props (width / rail-width) */

/* JazelAppContainer (and Vuetify) wrap main content in a v-container with breakpoint max-widths
   (e.g., 1200px). That prevents the content area from expanding when the nav is collapsed.
   Remove that cap so the app can use the reclaimed horizontal space. */
:deep(main.v-main > .v-container) {
  max-width: none !important;
}
</style>
